<template>
  <v-container class="fill-height with-fancy-bg">
    <v-row
      align="center"
      class="fill-height"
      justify="center">
      <v-col>
        <v-progress-circular
          v-if="!email"
          :size="50"
          color="primary"
          indeterminate></v-progress-circular>
        <v-card
          class="mx-auto"
          max-width="688px">
          <div class="right-language-switcher">
            <LanguageSwitcher />
          </div>
          <v-card-title class="text-center mt-10 auth-forms-top-title">
            <span class="text-primary">{{ $t("views.login.WelcomeTop") }}</span>
            <br />
            {{ $t("views.login.WelcomeBottom") }}
          </v-card-title>
          <v-card-subtitle
            v-if="errorMessage"
            class="auth-forms-sub-title text-center text-red">
            {{ $t("actions.error-message") }}
            {{ $t("actions.error-responses." + errorMessage) }}
          </v-card-subtitle>
          <v-card-subtitle
            v-else
            class="auth-forms-sub-title text-center">
            {{ $t("views.register.CreateYourAccount") }}
          </v-card-subtitle>

          <v-card-text>
            <v-form
              v-model="valid"
              style="overflow: unset">
              <v-text-field
                style="pointer-events: none"
                v-model="email"
                class="my-2"
                disabled
                density="default"
                :label="$t('entities.user.fields.email')"
                type="email"
                data-cy="signup-email" />
              <v-text-field
                data-cy="given-name"
                class="my-2"
                autocomplete="given-name"
                :label="$t('entities.user.fields.first_name')"
                v-model="name" />
              <v-text-field
                data-cy="family-name"
                class="my-2"
                autocomplete="family-name"
                :label="$t('entities.user.fields.last_name')"
                v-model="surname" />
              <v-text-field
                v-model="password"
                :append-inner-icon="!showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                autocomplete="current-password"
                class="mb-2"
                clearable
                density="default"
                :label="$t('entities.user.fields.password')"
                :persistent-placeholder="false"
                :rules="passwordRules"
                :type="showPassword ? 'input' : 'password'"
                @click:append-inner="showPassword = !showPassword"
                data-cy="signup-password" />

              <v-text-field
                v-model="repeatPassword"
                :append-inner-icon="
                  !showRepeatPassword ? 'mdi-eye-off' : 'mdi-eye'
                "
                autocomplete="current-password"
                class="mb-6"
                clearable
                density="default"
                :label="$t('entities.user.fields.password_confirmation')"
                :persistent-placeholder="false"
                :rules="[...passwordRules, ...repeatPasswordRules]"
                :type="showRepeatPassword ? 'input' : 'password'"
                @click:append-inner="showRepeatPassword = !showRepeatPassword"
                data-cy="signup-password-repeat" />

              <v-row class="mt-">
                <v-col class="text-center">
                  <v-btn
                    block
                    class="text-none text-subtitle-1"
                    color="primary"
                    :disabled="!valid || !signupButtonEnabled"
                    prepend-icon="mdi-check"
                    rounded="xl"
                    size="large"
                    variant="flat"
                    @click="signup"
                    data-cy="signup-submit">
                    {{ $t("actions.submit") }}
                  </v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="text-center">
                  {{ $t("views.register.AlreadyHaveAccount") }}
                  <router-link
                    class="mr-4 auth-forms-sub-title"
                    :to="{ name: 'login' }">
                    {{ $t("views.register.Login") }}
                  </router-link>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useTranslation } from "i18next-vue";

import LanguageSwitcher from "../../components/tools/LanguageSwitcher.vue";

import { useUserRules } from "../../validations/user.js";
import { useAuthStore } from "@/stores/authStore";
import { onMounted } from "vue";
import axios from "axios";
import { useSnackbar } from "vuetify-use-dialog";

const createSnackbar = useSnackbar();

const { t } = useTranslation();
const authStore = useAuthStore();
const router = useRouter();
const props = defineProps({
  token: String
});

const valid = ref(true);
const email = ref("");
const password = ref("");
const repeatPassword = ref("");
const showPassword = ref(false);
const showRepeatPassword = ref(false);
const errorMessage = ref(null);
const signupButtonEnabled = ref(true);

const name = ref("");
const surname = ref("");

const { emailRules, passwordRules } = useUserRules(t);

const repeatPasswordRules = ref([
  (v) => v === password.value || t("validations.passwords-dont-match")
]);

onMounted(() => {
  axios
    .get("/auth/get-user/" + props.token)
    .then((response) => {
      email.value = response.data.email;
    })
    .catch((error) => {
      console.error("Error fetching user data:", error.response.data);
      createSnackbar({
        text: t("responses." + error.response.data),
        snackbarProps: { color: "error", timeout: 3000 },
        showCloseButton: false
      });

      router.push({ name: "login" });
    });
});

function signup() {
  signupButtonEnabled.value = false;
  errorMessage.value = null;
  authStore
    .createAccount(password.value, props.token, {
      first_name: name.value,
      last_name: surname.value
    })
    .catch((error) => {
      console.log("error", error);
      errorMessage.value = error;
    })
    .finally(() => {
      signupButtonEnabled.value = true;
    });
}
</script>
